import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import {
  FaArrowRight,
  FaBookOpen,
  FaCheckCircle,
  FaChevronRight,
  FaClipboardCheck,
  FaEye,
  FaFilter,
  FaPlus,
  FaRegClock,
  FaSearch,
  FaTrash,
} from 'react-icons/fa';
import Swal from 'sweetalert2';

import { db } from '../../firebase/firebase';
import './LecturasAlumno.scss';

const limpiarHtml = (html = '') => html
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const contarPalabras = (lectura) => {
  const texto = limpiarHtml(lectura?.descripcion);
  return texto ? texto.split(' ').length : 0;
};

const formatearFecha = (valor) => {
  try {
    const fecha = valor?.toDate?.() || (valor ? new Date(valor) : null);
    if (!fecha || Number.isNaN(fecha.getTime())) return 'Fecha no registrada';
    return new Intl.DateTimeFormat('es-CO', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(fecha);
  } catch {
    return 'Fecha no registrada';
  }
};

const obtenerMarcaTiempo = (respuesta) => {
  const valor = respuesta?.completedAt;
  if (typeof valor?.toMillis === 'function') return valor.toMillis();
  const fecha = valor?.toDate?.() || (valor ? new Date(valor) : null);
  return fecha && !Number.isNaN(fecha.getTime()) ? fecha.getTime() : 0;
};

const tieneRespuestaLibre = (lectura) => Boolean(
  lectura?.freeResponseEvaluation?.title
  || limpiarHtml(lectura?.freeResponseEvaluation?.description)
);

const obtenerDetalleRespuestas = (respuesta, lectura) => {
  const detalle = [];

  if (respuesta?.selectedAnswers && typeof respuesta.selectedAnswers === 'object') {
    Object.entries(respuesta.selectedAnswers).forEach(([preguntaIndice, opcionIndice]) => {
      const indicePregunta = Number(preguntaIndice);
      const indiceOpcion = Number(opcionIndice);
      const evaluacion = lectura?.evaluations?.[indicePregunta];
      detalle.push({
        tipo: 'seleccion',
        numero: indicePregunta + 1,
        pregunta: evaluacion?.title || `Pregunta ${indicePregunta + 1}`,
        respuesta: evaluacion?.answers?.[indiceOpcion] || 'Sin respuesta registrada',
      });
    });
  }

  if (Array.isArray(respuesta?.respuestas)) {
    respuesta.respuestas.forEach((item, indice) => {
      if (item?.freeResponseText !== undefined) {
        detalle.push({
          tipo: 'libre',
          numero: indice + 1,
          pregunta: lectura?.freeResponseEvaluation?.title || 'Respuesta libre',
          descripcion: lectura?.freeResponseEvaluation?.description || '',
          respuesta: item.freeResponseText || 'Sin respuesta registrada',
        });
        return;
      }

      const indicePregunta = Math.max(Number(item?.step || indice + 1) - 1, 0);
      const evaluacion = lectura?.evaluations?.[indicePregunta];
      detalle.push({
        tipo: 'seleccion',
        numero: indicePregunta + 1,
        pregunta: evaluacion?.title || `Pregunta ${indicePregunta + 1}`,
        respuesta: item?.selectedOption || 'Sin respuesta registrada',
      });
    });
  }

  return detalle;
};

const LecturasAlumno = ({ id }) => {
  const [lecturas, setLecturas] = useState([]);
  const [lecturasAsignadas, setLecturasAsignadas] = useState([]);
  const [respuestas, setRespuestas] = useState([]);
  const [vista, setVista] = useState('asignacion');
  const [nivel, setNivel] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [lecturaElegida, setLecturaElegida] = useState(null);
  const [respuestaElegidaId, setRespuestaElegidaId] = useState(null);
  const [lecturaPreview, setLecturaPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let activo = true;

    const cargar = async () => {
      if (!id || !db) {
        setError('No fue posible identificar al alumno.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const [lecturasSnapshot, asignadasSnapshot, respuestasSnapshot] = await Promise.all([
          getDocs(collection(db, 'lecturasAdicionales')),
          getDoc(doc(db, 'alumnosLecturas', id)),
          getDocs(query(collection(db, 'respuestaLecturasAdicionales'), where('userid', '==', id))),
        ]);

        if (!activo) return;
        const catalogo = lecturasSnapshot.docs
          .map((lecturaDoc) => ({
            id: lecturaDoc.id,
            ...lecturaDoc.data(),
            level: String(lecturaDoc.data()?.level || ''),
          }))
          .sort((a, b) => (a.posicion || 0) - (b.posicion || 0));
        const idsDisponibles = new Set(catalogo.map((lectura) => lectura.id));
        const asignadas = (asignadasSnapshot.data()?.lecturasAsignadas || [])
          .filter((lectura) => idsDisponibles.has(lectura.id))
          .map((lectura) => catalogo.find((item) => item.id === lectura.id) || lectura);
        const intentos = respuestasSnapshot.docs
          .map((respuestaDoc) => ({ id: respuestaDoc.id, ...respuestaDoc.data() }))
          .sort((a, b) => obtenerMarcaTiempo(b) - obtenerMarcaTiempo(a));

        setLecturas(catalogo);
        setLecturasAsignadas(asignadas);
        setRespuestas(intentos);
        setRespuestaElegidaId(intentos[0]?.id || null);
      } catch (cargaError) {
        console.error('Error cargando lecturas adicionales del alumno:', cargaError);
        if (activo) setError('No se pudieron cargar las lecturas adicionales. Inténtalo nuevamente.');
      } finally {
        if (activo) setLoading(false);
      }
    };

    cargar();
    return () => { activo = false; };
  }, [id]);

  const lecturasMap = useMemo(
    () => Object.fromEntries(lecturas.map((lectura) => [lectura.id, lectura])),
    [lecturas]
  );

  const idsAsignados = useMemo(
    () => new Set(lecturasAsignadas.map((lectura) => lectura.id)),
    [lecturasAsignadas]
  );

  const lecturasFiltradas = useMemo(() => {
    const termino = busqueda.trim().toLocaleLowerCase('es');
    return lecturas.filter((lectura) => (
      (!nivel || lectura.level === nivel)
      && (!termino || `${lectura.nombre} ${limpiarHtml(lectura.descripcion)}`.toLocaleLowerCase('es').includes(termino))
    ));
  }, [busqueda, lecturas, nivel]);

  const respuestaElegida = respuestas.find((respuesta) => respuesta.id === respuestaElegidaId) || respuestas[0];
  const lecturaRespondida = respuestaElegida ? lecturasMap[respuestaElegida.lecturaId] : null;
  const detalleRespuestas = obtenerDetalleRespuestas(respuestaElegida, lecturaRespondida);
  const lecturasCompletadas = new Set(respuestas.map((respuesta) => respuesta.lecturaId)).size;

  const guardarAsignadas = async (nuevasLecturas) => {
    await setDoc(doc(db, 'alumnosLecturas', id), {
      userid: id,
      lecturasAsignadas: nuevasLecturas,
    }, { merge: true });
    setLecturasAsignadas(nuevasLecturas);
  };

  const asignarLectura = async (lectura = lecturaElegida) => {
    if (!lectura || idsAsignados.has(lectura.id)) return;
    try {
      await guardarAsignadas([...lecturasAsignadas, lectura]);
      setLecturaElegida(null);
      Swal.fire({
        icon: 'success',
        title: 'Lectura asignada',
        text: `“${lectura.nombre}” ya está disponible para el alumno.`,
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (asignacionError) {
      console.error('Error asignando lectura:', asignacionError);
      Swal.fire({ icon: 'error', title: 'No se pudo asignar la lectura' });
    }
  };

  const quitarLectura = async (lectura) => {
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: '¿Quitar esta lectura?',
      text: `El alumno dejará de ver “${lectura.nombre}”. Sus respuestas guardadas no se eliminarán.`,
      showCancelButton: true,
      confirmButtonText: 'Quitar lectura',
      cancelButtonText: 'Conservar',
      confirmButtonColor: '#b42318',
    });
    if (!confirmacion.isConfirmed) return;

    try {
      await guardarAsignadas(lecturasAsignadas.filter((item) => item.id !== lectura.id));
    } catch (eliminacionError) {
      console.error('Error quitando lectura:', eliminacionError);
      Swal.fire({ icon: 'error', title: 'No se pudo quitar la lectura' });
    }
  };

  if (loading) {
    return (
      <div className="lecturas-alumno__state" role="status">
        <Spinner animation="border" />
        <span>Cargando lecturas y respuestas...</span>
      </div>
    );
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="lecturas-alumno">
      <header className="lecturas-alumno__intro">
        <div className="lecturas-alumno__intro-copy">
          <span className="lecturas-alumno__eyebrow">Seguimiento de comprensión lectora</span>
          <h2>Lecturas adicionales</h2>
          <p>Asigna material y revisa, en contexto, cada respuesta que el alumno entregó.</p>
        </div>
        <div className="lecturas-alumno__summary" aria-label="Resumen de lecturas">
          <div><strong>{lecturasAsignadas.length}</strong><span>Asignadas</span></div>
          <div><strong>{lecturasCompletadas}</strong><span>Completadas</span></div>
          <div><strong>{respuestas.length}</strong><span>Intentos</span></div>
        </div>
      </header>

      <div className="lecturas-alumno__switcher" role="tablist" aria-label="Gestión de lecturas">
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'asignacion'}
          className={vista === 'asignacion' ? 'is-active' : ''}
          onClick={() => setVista('asignacion')}
        >
          <FaBookOpen aria-hidden="true" /> Asignación
          <span>{lecturasAsignadas.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'respuestas'}
          className={vista === 'respuestas' ? 'is-active' : ''}
          onClick={() => setVista('respuestas')}
        >
          <FaClipboardCheck aria-hidden="true" /> Respuestas del alumno
          <span>{respuestas.length}</span>
        </button>
      </div>

      {vista === 'asignacion' ? (
        <div className="lecturas-asignacion" role="tabpanel">
          <section className="lecturas-panel lecturas-catalogo">
            <div className="lecturas-panel__heading">
              <div><span>Biblioteca</span><h3>Selecciona una lectura</h3></div>
              <small>{lecturasFiltradas.length} disponibles</small>
            </div>

            <div className="lecturas-filtros">
              <label className="lecturas-busqueda">
                <span className="visually-hidden">Buscar lectura</span>
                <FaSearch aria-hidden="true" />
                <input
                  type="search"
                  value={busqueda}
                  onChange={(event) => setBusqueda(event.target.value)}
                  placeholder="Buscar por título o contenido"
                />
              </label>
              <label className="lecturas-nivel">
                <span className="visually-hidden">Filtrar por nivel</span>
                <FaFilter aria-hidden="true" />
                <select value={nivel} onChange={(event) => setNivel(event.target.value)}>
                  <option value="">Todos los niveles</option>
                  <option value="1">Nivel 1</option>
                  <option value="2">Nivel 2</option>
                  <option value="3">Nivel 3</option>
                </select>
              </label>
            </div>

            <div className="lecturas-catalogo__list">
              {lecturasFiltradas.length === 0 ? (
                <div className="lecturas-empty"><FaBookOpen /><strong>No hay coincidencias</strong><span>Prueba otra búsqueda o nivel.</span></div>
              ) : lecturasFiltradas.map((lectura) => {
                const seleccionada = lecturaElegida?.id === lectura.id;
                const asignada = idsAsignados.has(lectura.id);
                const preguntas = lectura.evaluations?.length || 0;
                return (
                  <button
                    type="button"
                    key={lectura.id}
                    className={`lectura-catalogo-item ${seleccionada ? 'is-selected' : ''}`}
                    onClick={() => setLecturaElegida(lectura)}
                    aria-pressed={seleccionada}
                  >
                    <span className="lectura-catalogo-item__level">N{lectura.level || '—'}</span>
                    <span className="lectura-catalogo-item__copy">
                      <strong>{lectura.nombre || 'Lectura sin título'}</strong>
                      <small>
                        {contarPalabras(lectura)} palabras · {preguntas} {preguntas === 1 ? 'pregunta' : 'preguntas'}
                        {tieneRespuestaLibre(lectura) ? ' + respuesta libre' : ''}
                      </small>
                    </span>
                    {asignada ? <span className="lectura-status"><FaCheckCircle /> Asignada</span> : <FaChevronRight />}
                  </button>
                );
              })}
            </div>
          </section>

          <aside className="lecturas-panel lectura-seleccion">
            {lecturaElegida ? (
              <>
                <div className="lectura-seleccion__cover" aria-hidden="true"><FaBookOpen /></div>
                <span className="lecturas-alumno__eyebrow">Nivel {lecturaElegida.level || 'sin definir'}</span>
                <h3>{lecturaElegida.nombre}</h3>
                <p>{limpiarHtml(lecturaElegida.descripcion).slice(0, 220) || 'Esta lectura no tiene una descripción previa.'}{limpiarHtml(lecturaElegida.descripcion).length > 220 ? '…' : ''}</p>
                <div className="lectura-seleccion__facts">
                  <span><strong>{contarPalabras(lecturaElegida)}</strong> palabras</span>
                  <span><strong>{lecturaElegida.evaluations?.length || 0}</strong> selección simple</span>
                  <span><strong>{tieneRespuestaLibre(lecturaElegida) ? 'Sí' : 'No'}</strong> respuesta libre</span>
                </div>
                <div className="lectura-seleccion__actions">
                  <Button variant="outline-primary" onClick={() => setLecturaPreview(lecturaElegida)}><FaEye /> Ver lectura</Button>
                  <Button onClick={() => asignarLectura()} disabled={idsAsignados.has(lecturaElegida.id)}>
                    {idsAsignados.has(lecturaElegida.id) ? <><FaCheckCircle /> Ya asignada</> : <><FaPlus /> Asignar lectura</>}
                  </Button>
                </div>
              </>
            ) : (
              <div className="lectura-seleccion__empty">
                <FaArrowRight />
                <h3>Elige una lectura</h3>
                <p>Aquí verás su contenido, extensión y todos los tipos de evaluación antes de asignarla.</p>
              </div>
            )}
          </aside>

          <section className="lecturas-panel lecturas-asignadas">
            <div className="lecturas-panel__heading">
              <div><span>Plan del alumno</span><h3>Lecturas asignadas</h3></div>
              <small>{lecturasAsignadas.length} en total</small>
            </div>
            {lecturasAsignadas.length === 0 ? (
              <div className="lecturas-empty"><FaRegClock /><strong>Aún no hay lecturas asignadas</strong><span>Selecciona una lectura de la biblioteca para comenzar.</span></div>
            ) : (
              <div className="lecturas-asignadas__grid">
                {lecturasAsignadas.map((lectura) => {
                  const completada = respuestas.some((respuesta) => respuesta.lecturaId === lectura.id);
                  return (
                    <article key={lectura.id} className="lectura-asignada-card">
                      <span className={`lectura-asignada-card__status ${completada ? 'is-complete' : ''}`}>
                        {completada ? <><FaCheckCircle /> Completada</> : <><FaRegClock /> Pendiente</>}
                      </span>
                      <h4>{lectura.nombre}</h4>
                      <p>Nivel {lectura.level || '—'} · {lectura.evaluations?.length || 0} preguntas</p>
                      <div>
                        <button type="button" onClick={() => setLecturaPreview(lectura)}><FaEye /> Visualizar</button>
                        {completada && <button type="button" onClick={() => setVista('respuestas')}><FaClipboardCheck /> Revisar</button>}
                        <button type="button" className="is-danger" onClick={() => quitarLectura(lectura)} aria-label={`Quitar ${lectura.nombre}`}><FaTrash /></button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      ) : (
        <div className="lecturas-revision" role="tabpanel">
          {respuestas.length === 0 ? (
            <div className="lecturas-panel lecturas-empty lecturas-empty--large">
              <FaClipboardCheck />
              <strong>El alumno aún no ha enviado respuestas</strong>
              <span>Cuando complete una lectura, el intento aparecerá aquí con todas sus respuestas.</span>
            </div>
          ) : (
            <>
              <aside className="lecturas-panel lecturas-intentos">
                <div className="lecturas-panel__heading"><div><span>Historial</span><h3>Intentos entregados</h3></div></div>
                <div className="lecturas-intentos__list">
                  {respuestas.map((respuesta, indice) => {
                    const lectura = lecturasMap[respuesta.lecturaId];
                    const activa = respuesta.id === respuestaElegida?.id;
                    return (
                      <button type="button" key={respuesta.id} className={activa ? 'is-active' : ''} onClick={() => setRespuestaElegidaId(respuesta.id)}>
                        <span className="lecturas-intentos__number">{String(respuestas.length - indice).padStart(2, '0')}</span>
                        <span><strong>{lectura?.nombre || 'Lectura no disponible'}</strong><small>{formatearFecha(respuesta.completedAt)}</small></span>
                        <FaChevronRight aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              </aside>

              <section className="lecturas-panel lectura-respuesta">
                <header className="lectura-respuesta__header">
                  <div>
                    <span className="lecturas-alumno__eyebrow">Intento completado</span>
                    <h3>{lecturaRespondida?.nombre || 'Lectura no disponible'}</h3>
                    <p>{formatearFecha(respuestaElegida?.completedAt)}</p>
                  </div>
                  {lecturaRespondida && <Button variant="outline-primary" onClick={() => setLecturaPreview(lecturaRespondida)}><FaEye /> Ver lectura</Button>}
                </header>

                <div className="lectura-respuesta__meta">
                  <span>Nivel <strong>{lecturaRespondida?.level || '—'}</strong></span>
                  <span>Respuestas <strong>{detalleRespuestas.length}</strong></span>
                  <span>Tipo <strong>{detalleRespuestas.some((item) => item.tipo === 'libre') ? 'Mixta' : 'Selección'}</strong></span>
                </div>

                <div className="lectura-respuesta__list">
                  {detalleRespuestas.length === 0 ? (
                    <Alert variant="light">Este intento no contiene respuestas reconocibles.</Alert>
                  ) : detalleRespuestas.map((item, indice) => (
                    <article key={`${item.tipo}-${item.numero}-${indice}`} className={`respuesta-card respuesta-card--${item.tipo}`}>
                      <div className="respuesta-card__marker">{item.tipo === 'libre' ? 'RL' : String(item.numero).padStart(2, '0')}</div>
                      <div className="respuesta-card__content">
                        <span>{item.tipo === 'libre' ? 'Respuesta libre' : 'Selección simple'}</span>
                        <h4>{item.pregunta}</h4>
                        {item.descripcion && <div className="respuesta-card__description" dangerouslySetInnerHTML={{ __html: item.descripcion }} />}
                        <div className="respuesta-card__answer"><FaCheckCircle aria-hidden="true" /><p>{item.respuesta}</p></div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      )}

      <Modal
        show={Boolean(lecturaPreview)}
        onHide={() => setLecturaPreview(null)}
        size="xl"
        centered
        scrollable
        dialogClassName="lectura-preview-modal"
      >
        <Modal.Header closeButton>
          <div><span>Vista de lectura · Nivel {lecturaPreview?.level || '—'}</span><Modal.Title>{lecturaPreview?.nombre || 'Lectura'}</Modal.Title></div>
        </Modal.Header>
        <Modal.Body>
          <div className={`lectura-preview__body ${String(lecturaPreview?.columnas) === '2' ? 'has-columns' : ''}`} dangerouslySetInnerHTML={{ __html: lecturaPreview?.descripcion || '<p>Sin contenido disponible.</p>' }} />
          <section className="lectura-preview__evaluation">
            <div className="lecturas-panel__heading"><div><span>Evaluación</span><h3>Preguntas de la actividad</h3></div></div>
            {(lecturaPreview?.evaluations || []).map((evaluacion, indice) => (
              <article key={`${evaluacion.title}-${indice}`}>
                <strong>{indice + 1}. {evaluacion.title || 'Pregunta sin título'}</strong>
                <ul>{(evaluacion.answers || []).filter(Boolean).map((respuesta) => <li key={respuesta}>{respuesta}</li>)}</ul>
              </article>
            ))}
            {tieneRespuestaLibre(lecturaPreview) && (
              <article className="is-free-response">
                <strong>Respuesta libre. {lecturaPreview.freeResponseEvaluation?.title || 'Pregunta abierta'}</strong>
                {lecturaPreview.freeResponseEvaluation?.description && <div dangerouslySetInnerHTML={{ __html: lecturaPreview.freeResponseEvaluation.description }} />}
              </article>
            )}
            {!lecturaPreview?.evaluations?.length && !tieneRespuestaLibre(lecturaPreview) && <p className="text-muted">Esta lectura no tiene evaluaciones configuradas.</p>}
          </section>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LecturasAlumno;
